import React, { useEffect, useReducer, useRef, useState } from 'react';
import { KeyCode, SensitiveData } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { arrayOf, bool, eventListener, expression, extendComponent, oneOf, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { validators } from '@philips-internal/oswald-ui-model';
import { classNames, defaultClasses, enumValues, isContentfulString, isEnumValid, isFunction, trigger } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as PlusIcon } from '../../../resources/assets/components/DLS_Plus.svg';
import { hideSensitiveData } from '../../utils/hide-sensitive-data.js';
import { useOptionalField } from '../field/context/field-context.js';
import { Input } from '../input/input.js';
import { Token } from '../token/token.js';
import { TokenShape, TokenSize } from '../token-list/token-list-constants.js';
import { TokenInputAPI } from './token-input-api.js';
import './token-input.css';
const awesomeProps = {
    api: TokenInputAPI,
    type: ComponentType.DATA_ENTRY,
};
const runUniqueValidations = (tokens) => {
    const validationRunner = ModelService.validate(tokens);
    validationRunner.apply(validators.UNIQUE);
    return validationRunner.run();
};
function tokenStateReducer(data, partialData) {
    return {
        ...data,
        ...partialData,
    };
}
const addTokenInitialState = { editing: false, valid: true, value: '' };
function AddToken(props) {
    const [{ editing, valid, value }, setState] = useReducer(tokenStateReducer, addTokenInitialState);
    const fieldRef = useRef(null);
    const validate = (token) => {
        return runUniqueValidations([...props.tokens, token]).every(validation => validation.valid);
    };
    const onChange = (event) => {
        setState({ valid: validate(event.value), value: event.value });
    };
    const addToken = () => {
        if (!isContentfulString(value)) {
            setState({ editing: false, valid: true });
            return;
        }
        if (!validate(value)) {
            setState({ valid: false });
            return;
        }
        props.onAddToken(value);
        setState(addTokenInitialState);
        fieldRef.current?.focus();
    };
    const onInputKeyUp = (event) => {
        event.stopPropagation();
        switch (event.keyCode) {
            case KeyCode.ESC: {
                setState(addTokenInitialState);
                fieldRef.current?.focus();
                break;
            }
            case KeyCode.ENTER: {
                addToken();
                break;
            }
        }
    };
    const onTokenKeyUp = (event) => {
        event.stopPropagation();
        if (!editing && event.keyCode === KeyCode.ENTER) {
            setState({ editing: true });
        }
    };
    const content = editing ? (React.createElement("div", { className: "c-token-input__add-new__input" },
        React.createElement(Input, { autoFocus: true, onChange: onChange, onKeyUp: onInputKeyUp, suppressValidations: true, valid: valid, value: value }))) : (React.createElement("div", { className: "c-token__content c-token-input__add-new__button", onClick: trigger(setState, { editing: true }) },
        React.createElement(PlusIcon, { className: "c-token__icon" }),
        React.createElement("span", { className: "c-token__text" }, MessageService.translate(props.addExpression))));
    const className = classNames({
        'c-token': true,
        'c-token-input__add-new': true,
        'c-token-input__add-new--editing': editing,
        [`c-token--${props.shape}`]: isEnumValid(TokenShape, props.shape),
        [`c-token--${props.size}`]: isEnumValid(TokenSize, props.size),
    });
    return (React.createElement("div", { className: className, onKeyUp: onTokenKeyUp, ref: fieldRef, tabIndex: 0 }, content));
}
function TokenInput(props) {
    const fieldContext = useOptionalField();
    const [deletionHistory, setDeletionHistory] = useState([]);
    const inputRef = useRef(null);
    const className = classNames({
        'c-token-input': true,
        'u-scrollbar': true,
        [`c-token-input--${props.tokenProps?.size}`]: isEnumValid(TokenSize, props.tokenProps?.size),
        [`${props.className}`]: Boolean(props.className),
        ...defaultClasses({
            enabled: Boolean(props.enabled),
            readOnly: Boolean(props.readOnly),
            valid: Boolean(props.valid),
        }),
    });
    const updateValidations = (value) => {
        if (!fieldContext) {
            return;
        }
        const validations = runUniqueValidations(value);
        fieldContext.updateChildValidations(validations);
    };
    useEffect(() => {
        updateValidations(props.value ?? []);
    }, []);
    const addToken = (newToken, position) => {
        if (!isFunction(props.onChange)) {
            return;
        }
        const newValue = [...(props.value ?? [])];
        newValue.splice(position ?? newValue.length, 0, newToken);
        updateValidations(newValue);
        props.onChange(new ChangeEvent({
            oldValue: props.value,
            value: newValue,
        }));
    };
    const removeToken = (tokenValue, index) => {
        if (!isFunction(props.onChange)) {
            return;
        }
        setDeletionHistory(prevDeletionHistory => [...prevDeletionHistory, { index, value: tokenValue }]);
        props.onChange(new ChangeEvent({
            oldValue: props.value,
            value: props.value?.filter(value => value !== tokenValue) ?? [],
        }));
        inputRef.current?.focus();
    };
    const onTokenKeyUp = (event, value, index) => {
        switch (event.keyCode) {
            case KeyCode.DELETE_BTN:
            case KeyCode.DEL:
                removeToken(value, index);
                break;
        }
    };
    const onFieldKeyUp = (event) => {
        if (!event.ctrlKey || event.keyCode !== KeyCode.Z) {
            return;
        }
        const lastDeletedToken = deletionHistory.pop();
        if (lastDeletedToken) {
            addToken(lastDeletedToken.value, lastDeletedToken.index);
        }
    };
    const editing = !props.readOnly && props.enabled;
    return (React.createElement("div", { "aria-label": MessageService.translate(props.name ?? ''), className: className, onKeyUp: onFieldKeyUp, ref: inputRef, role: "list", tabIndex: 0 },
        props.value?.map((value, index) => (React.createElement("div", { "aria-label": hideSensitiveData(value, props.sensitiveData ?? SensitiveData.NONE), className: "c-token-input__token-wrapper", key: value, onKeyUp: event => onTokenKeyUp(event, value, index), role: "listitem", tabIndex: 0 },
            React.createElement(Token, { color: props.tokenProps?.color, dismissible: editing, enabled: editing, labelColor: props.tokenProps?.labelColor, onDismiss: event => removeToken(event.value?.value ?? '', index), sensitiveData: editing ? SensitiveData.NONE : props.sensitiveData, shape: props.tokenProps?.shape, size: props.tokenProps?.size, strikethrough: props.strikethrough, value: value })))),
        editing ? (React.createElement(AddToken, { addExpression: props.addExpression ?? TokenInput.defaultProps.addExpression, onAddToken: addToken, shape: props.tokenProps?.shape, size: props.tokenProps?.size, tokens: props.value ?? [] })) : null));
}
TokenInput.propTypes = {
    addExpression: expression,
    className: string,
    enabled: bool,
    name: string,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    readOnly: bool,
    sensitiveData: oneOf(enumValues(SensitiveData)),
    strikethrough: bool,
    tokenProps: extendComponent('Token', [
        'color',
        'labelColor',
        'shape',
        'size',
    ], [
        'dismissible',
        'enabled',
        'formatters',
        'icon',
        'sensitiveData',
        'strikethrough',
        'value',
    ]),
    valid: bool,
    value: arrayOf(string),
};
TokenInput.defaultProps = {
    addExpression: 'tws.token-input.add-token',
    enabled: true,
    name: 'tws.token-input.name',
    sensitiveData: SensitiveData.NONE,
    tokenProps: {
        shape: TokenShape.CIRCLE,
        size: TokenSize.NORMAL,
    },
};
AwesomeComponent(awesomeProps)(TokenInput);
export { TokenInput, };
//# sourceMappingURL=token-input.js.map